// The phone number field, shared by every step that collects one.
//
// It used to live inside LeadCapture. The Confirm step now asks for the phone
// in the international markets (2026-10-10), and the dial-code handling —
// an editable code where the market allows it, foreign numbers validated
// against the code the customer typed rather than the market's own — is
// exactly the kind of thing that drifts when it exists twice. One component,
// two call sites, the same number accepted on both.
//
// Ownership of the value stays in the booking reducer (`state.phone` and
// `state.dialCodeOverride`); this component only renders and dispatches.

import styles from '../styles/widget.module.css';
import type { BookingAction, BookingState } from '../state/machine';
import { countryConfigOrDefault } from '../config/countries';
import {
  effectiveDialCode,
  foreignInputValue,
  formatForeignFromInput,
  formatPhoneFromInput,
  isDialCodeOverridden,
  normalizeDialCode,
  phoneDialCodeLabel,
  phoneInputValue,
  phonePlaceholder,
} from '../utils/phone';

interface PhoneFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  /** Shows the red hairline. The caller decides when, from its own touched set. */
  invalid?: boolean;
  /** Called on blur of the number or the dial code, so the caller can mark it touched. */
  onTouched?: () => void;
  /** Overrides the market's format hint — the inline card describes the field instead. */
  placeholder?: string;
  /** Element id; the label's `htmlFor` follows it. Two fields on one page need two ids. */
  id?: string;
  label?: React.ReactNode;
}

export function PhoneField({
  state,
  dispatch,
  invalid = false,
  onTouched,
  placeholder,
  id = 'cc-phone',
  label = 'Phone Number*',
}: PhoneFieldProps) {
  const { dialCodeEditable } = countryConfigOrDefault(state.country);

  // The code in force, and whether the customer has moved it off the market's.
  // Everywhere except the UK the override is always null and both of these are
  // the market's own values.
  //
  // Three values, not one, because they answer different questions:
  //   dialCodeValue  what the input shows. `null` means untouched, so the
  //                  market's own. An empty STRING means the customer cleared
  //                  it, which has to survive — collapsing empty back to the
  //                  market default is what made the field impossible to edit:
  //                  select-all + delete instantly restored "44", so a new
  //                  code could only ever be appended to it.
  //   activeDialCode what formatting and validation use. Empty falls back to
  //                  the market, since a number cannot be parsed against no
  //                  code at all.
  //   foreignCode    whether to leave the market's grouping rules behind.
  const marketDialCode = countryConfigOrDefault(state.country).phone.dialCode;
  const dialCodeValue = state.dialCodeOverride ?? marketDialCode;
  const activeDialCode = effectiveDialCode(state.country, state.dialCodeOverride);
  const foreignCode = isDialCodeOverridden(state.country, state.dialCodeOverride);

  const dialCode = phoneDialCodeLabel(state.country);

  const phoneInput = (
    <input
      id={id}
      name="tel"
      className={styles.input}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={
        foreignCode
          ? foreignInputValue(state.phone, activeDialCode)
          : phoneInputValue(state.phone, state.country)
      }
      placeholder={placeholder ?? phonePlaceholder(state.country)}
      aria-invalid={invalid}
      onBlur={onTouched}
      onChange={(e) =>
        dispatch({
          type: 'SET_LEAD',
          payload: {
            phone: foreignCode
              ? formatForeignFromInput(e.target.value, activeDialCode)
              : formatPhoneFromInput(e.target.value, state.country),
          },
        })
      }
    />
  );

  // Changing the code re-writes the number under it. The digits the customer
  // typed are theirs and are kept; only the code in front of them changes, so
  // switching +44 → +353 mid-entry does not silently discard the number.
  const handleDialCodeChange = (raw: string) => {
    const next = normalizeDialCode(raw);
    const digits = foreignCode
      ? foreignInputValue(state.phone, activeDialCode)
      : phoneInputValue(state.phone, state.country);
    const nowForeign = next !== '' && next !== marketDialCode;
    dispatch({
      type: 'SET_LEAD',
      payload: {
        // The string, empty included. `next || null` was the bug: it turned a
        // cleared field back into "untouched", which renders as the market
        // code again.
        dialCodeOverride: next,
        phone: nowForeign
          ? formatForeignFromInput(digits, next)
          : formatPhoneFromInput(digits, state.country),
      },
    });
  };

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>{label}</label>
      {dialCode ? (
        <div className={styles.phoneGroup} data-invalid={invalid || undefined}>
          {dialCodeEditable ? (
            <span className={styles.phoneDialCode}>
              +
              <input
                className={styles.phoneDialCodeInput}
                type="text"
                inputMode="numeric"
                autoComplete="tel-country-code"
                aria-label="Country calling code"
                value={dialCodeValue}
                size={Math.max(2, dialCodeValue.length)}
                onChange={(e) => handleDialCodeChange(e.target.value)}
                onBlur={onTouched}
              />
            </span>
          ) : (
            <span className={styles.phoneDialCode} aria-hidden="true">{dialCode}</span>
          )}
          {phoneInput}
        </div>
      ) : (
        phoneInput
      )}
    </div>
  );
}
